import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";

const MODES = { SIGN_IN: "SIGN_IN", SIGN_UP: "SIGN_UP" };

export default function Login() {
  const { signIn, signUpWithInvite, authError } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState(MODES.SIGN_IN);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);

  const [form, setForm] = useState({
    email: "",
    password: "",
    displayName: "",
    inviteCode: "",
  });

  const updateField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setNotice(null);

    if (mode === MODES.SIGN_IN) {
      const { error } = await signIn({ email: form.email, password: form.password });
      setSubmitting(false);
      if (!error) navigate("/");
      return;
    }

    const { error } = await signUpWithInvite({
      email: form.email,
      password: form.password,
      displayName: form.displayName || form.email.split("@")[0],
      inviteCode: form.inviteCode,
    });
    setSubmitting(false);

    if (!error) {
      setNotice("PLAYER REGISTERED! Check your email to confirm your account, then insert coin to log in.");
      setMode(MODES.SIGN_IN);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-ink px-4 py-10">
      {/* Ambient arcade glow */}
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-magenta/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-72 w-72 rounded-full bg-cyan/20 blur-3xl" />

      <div className="relative z-10 w-full max-w-sm">
        <div className="mb-8 text-center">
          <p className="label-pixel mb-2 animate-pulse text-magenta">Insert Coin</p>
          <h1 className="pixel-heading text-lg text-mist sm:text-2xl">
            LEVEL UP<br />
            <span className="text-cyan">WEALTH</span>
          </h1>
        </div>

        <div className="panel p-6">
          <div className="mb-6 flex border-2 border-black">
            <button
              type="button"
              onClick={() => setMode(MODES.SIGN_IN)}
              className={`flex-1 py-2 font-display text-[9px] uppercase tracking-wide transition-colors ${
                mode === MODES.SIGN_IN ? "bg-magenta text-ink" : "bg-panel2 text-mist"
              }`}
            >
              Player 1
            </button>
            <button
              type="button"
              onClick={() => setMode(MODES.SIGN_UP)}
              className={`flex-1 py-2 font-display text-[9px] uppercase tracking-wide transition-colors ${
                mode === MODES.SIGN_UP ? "bg-cyan text-ink" : "bg-panel2 text-mist"
              }`}
            >
              New Player
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === MODES.SIGN_UP && (
              <div>
                <label className="label-pixel mb-1 block">Player Name</label>
                <input
                  className="input-pixel"
                  type="text"
                  placeholder="e.g. MoneyMage99"
                  value={form.displayName}
                  onChange={updateField("displayName")}
                />
              </div>
            )}

            <div>
              <label className="label-pixel mb-1 block">Email</label>
              <input
                className="input-pixel"
                type="email"
                required
                placeholder="you@example.com"
                value={form.email}
                onChange={updateField("email")}
              />
            </div>

            <div>
              <label className="label-pixel mb-1 block">Password</label>
              <input
                className="input-pixel"
                type="password"
                required
                minLength={8}
                placeholder="••••••••"
                value={form.password}
                onChange={updateField("password")}
              />
            </div>

            {mode === MODES.SIGN_UP && (
              <div>
                <label className="label-pixel mb-1 block">Family Invite Code</label>
                <input
                  className="input-pixel uppercase tracking-widest"
                  type="text"
                  required
                  placeholder="XXXX-XXXX"
                  value={form.inviteCode}
                  onChange={updateField("inviteCode")}
                />
                <p className="mt-1 text-[11px] text-mist/60">
                  This arcade is family-only for now. Ask the owner for your code.
                </p>
              </div>
            )}

            {authError && (
              <div className="animate-shake border-2 border-danger bg-danger/10 px-3 py-2 text-[11px] text-danger">
                {authError}
              </div>
            )}

            {notice && (
              <div className="border-2 border-lime bg-lime/10 px-3 py-2 text-[11px] text-lime">
                {notice}
              </div>
            )}

            <button type="submit" disabled={submitting} className="btn-magenta w-full disabled:opacity-50">
              {submitting ? "LOADING…" : mode === MODES.SIGN_IN ? "PRESS START" : "CREATE PLAYER"}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-[10px] text-mist/50">
          © {new Date().getFullYear()} Level Up Wealth Arcade — High scores are earned, not given.
        </p>
      </div>
    </div>
  );
}
